import { useMemo } from "react";
import type { Vistoria } from "@/types/vistorias";

interface AlertaPrioritario {
  id: string;
  titulo: string;
  descricao: string;
  nivel: string;
  quantidade: number;
  icone: string;
}

interface InspectionPriorityCardProps {
  vistorias: Vistoria[];
}

function computeAlerts(vistorias: Vistoria[]): AlertaPrioritario[] {
  const alerts: AlertaPrioritario[] = [];

  const critico = vistorias.filter((v) => v.classeRisco.toLowerCase() === "critico");
  if (critico.length > 0) {
    alerts.push({
      id: "risco-critico",
      titulo: "Risco Crítico",
      descricao: "Árvores com classe de risco crítico identificada",
      nivel: "critico",
      quantidade: critico.length,
      icone: "ri-error-warning-line",
    });
  }

  const alto = vistorias.filter((v) => v.classeRisco.toLowerCase() === "alto");
  if (alto.length > 0) {
    alerts.push({
      id: "risco-alto",
      titulo: "Risco Alto",
      descricao: "Árvores com classe de risco alto",
      nivel: "alto",
      quantidade: alto.length,
      icone: "ri-alert-line",
    });
  }

  const estadoRuim = vistorias.filter((v) => {
    const e = v.estadoFitossanitario.toLowerCase();
    return e === "ruim" || e === "critico" || e === "morto";
  });
  if (estadoRuim.length > 0) {
    alerts.push({
      id: "estado-critico",
      titulo: "Estado Fitossanitário Comprometido",
      descricao: "Árvores em estado ruim, crítico ou morto",
      nivel: estadoRuim.some((v) => v.estadoFitossanitario.toLowerCase() === "morto" || v.estadoFitossanitario.toLowerCase() === "critico") ? "critico" : "alto",
      quantidade: estadoRuim.length,
      icone: "ri-heart-pulse-line",
    });
  }

  const vencidas = vistorias.filter((v) => v.status === "Vencida");
  if (vencidas.length > 0) {
    alerts.push({
      id: "vencidas",
      titulo: "Vistorias Vencidas",
      descricao: "Vistorias com prazo de validade expirado",
      nivel: "alto",
      quantidade: vencidas.length,
      icone: "ri-time-line",
    });
  }

  return alerts;
}

export default function InspectionPriorityCard({ vistorias }: InspectionPriorityCardProps) {
  const alerts = useMemo(() => computeAlerts(vistorias), [vistorias]);

  const nivelConfig: Record<string, { bar: string; bg: string }> = {
    critico: { bar: "bg-danger-500", bg: "bg-danger-50" },
    alto: { bar: "bg-warning-500", bg: "bg-warning-50" },
    medio: { bar: "bg-caution-500", bg: "bg-caution-50" },
  };

  const totalCriticoAlto = alerts
    .filter((a) => a.nivel === "critico" || a.nivel === "alto")
    .reduce((s, a) => s + a.quantidade, 0);

  if (alerts.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-background-200/70 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-background-100 flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
            <i className="ri-check-line text-primary-500"></i>
          </span>
          <span className="text-sm font-semibold text-foreground-900 font-heading">Vistorias Prioritárias</span>
        </div>
        <div className="px-5 py-8 text-center">
          <p className="text-sm text-foreground-500">Nenhum alerta prioritário identificado</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-background-200/70 overflow-hidden">
      <div className="px-5 py-3.5 border-b border-background-100 flex items-center gap-2.5">
        <span className="w-8 h-8 rounded-lg bg-danger-50 flex items-center justify-center">
          <i className="ri-alert-line text-danger-500"></i>
        </span>
        <span className="text-sm font-semibold text-foreground-900 font-heading">Vistorias Prioritárias</span>
        <span className="badge bg-danger-100 text-danger-700 ml-auto">{totalCriticoAlto} itens</span>
      </div>
      <div className="divide-y divide-background-100">
        {alerts.map((alerta) => {
          const nc = nivelConfig[alerta.nivel] || nivelConfig.medio;
          return (
            <div key={alerta.id} className="px-5 py-3 hover:bg-background-50/60 transition-colors cursor-pointer flex items-start gap-3">
              <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${nc.bar}`}></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <i className={`${alerta.icone} text-xs text-foreground-500`}></i>
                  <h4 className="text-sm font-medium text-foreground-900 truncate">{alerta.titulo}</h4>
                </div>
                <p className="text-xs text-foreground-500 leading-relaxed">{alerta.descricao}</p>
              </div>
              <span className={`shrink-0 text-xs font-bold px-2 py-0.5 rounded-full ${nc.bg} ${alerta.nivel === "critico" ? "text-danger-700" : alerta.nivel === "alto" ? "text-warning-700" : "text-caution-700"}`}>
                {alerta.quantidade}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
